import { DrawingProperties, pinPropertyKey } from '@cadstrom/renderer';

import type { Component, Module, SignalId } from '@cadstrom/sigma-engine-shared';
import type { CadRendererZoomEventDetail } from '#src/utils/events.js';

/**
 * The fields of a {@link Module} needed to resolve a zoom/selection target. Declared as a narrow
 * subset so callers (and tests) can supply minimal fixtures.
 */
export type ZoomSelectionModule = Pick<
	Module,
	'ref' | 'name' | 'fullModuleName' | 'componentRef' | 'footprintRef' | 'pinNets'
>;

/**
 * The fields of a {@link Component} needed to resolve a zoom/selection target, keyed by component ref.
 */
export type ZoomSelectionComponents = Record<string, Pick<Component, 'signals' | 'symbols' | 'footprints'> | undefined>;

/**
 * A single property-based selection to apply to the renderer. The renderer selects every graphic
 * whose `property` equals `value`. For pins/pads the value is the compound key `"moduleRef/pinRef"`.
 */
export interface ZoomSelectionCommand {
	property: DrawingProperties;
	value: string;
}

/**
 * Whether a module actually contains a given pin/pad — either it is connected to a net (`pinNets`),
 * or the module's symbol/footprint definition declares it.
 *
 * This is the authoritative "does this instance have this pin/pad" check. Multi-unit parts (U1A/U1B)
 * split a component's pins across separate symbols/footprints, so an instance only contains the refs
 * its own symbol/footprint declares.
 *
 * @param module     - The design module (instance) to test.
 * @param components - The component map for symbol/footprint definition lookup.
 * @param ref        - The pin or pad ref.
 * @param kind       - Whether `ref` names a schematic symbol pin or a PCB footprint pad.
 * @returns `true` if the module is connected on `ref` or its definition declares it.
 */
export function moduleContainsPin(
	module: ZoomSelectionModule,
	components: ZoomSelectionComponents,
	ref: string,
	kind: 'symbolPin' | 'footprintPad',
): boolean {
	if (module.pinNets[ref] !== undefined) {
		return true;
	}
	const component = components[module.componentRef];
	if (kind === 'symbolPin') {
		return component?.symbols.find((s) => s.ref === module.footprintRef)?.pins.some((p) => p.ref === ref) ?? false;
	}
	return component?.footprints.find((f) => f.ref === module.footprintRef)?.pads.some((p) => p.ref === ref) ?? false;
}

/**
 * Finds the specific module instance that contains a given symbol pin or footprint pad ref.
 *
 * Pin/pad refs (e.g. "1") are unique only within a single module, so when several candidate modules
 * share a reference designator (multi-unit parts like U1A/U1B) we must pick the one that actually
 * contains the ref. Prefers an instance where the ref is connected to a net (via `pinNets`), then any
 * instance whose symbol/footprint definition declares it (so unconnected pins can still be located).
 *
 * @param pinRef     - The pin or pad ref to locate.
 * @param modules    - The candidate design modules (already filtered to the reference designator).
 * @param components - The component map for symbol/footprint definition lookup.
 * @param kind       - Whether `pinRef` names a schematic symbol pin or a PCB footprint pad.
 * @returns The instance that contains the ref, or `undefined` if no candidate contains it. Never
 *          guesses an instance that lacks the ref.
 */
export function findTargetModuleForPin(
	pinRef: string,
	modules: ZoomSelectionModule[],
	components: ZoomSelectionComponents,
	kind: 'symbolPin' | 'footprintPad',
): ZoomSelectionModule | undefined {
	const connected = modules.find((m) => m.pinNets[pinRef] !== undefined);
	if (connected !== undefined) {
		return connected;
	}

	return modules.find((m) => moduleContainsPin(m, components, pinRef, kind));
}

/**
 * Finds the specific module instance that contains a given signal's pins/pads.
 *
 * Multi-unit parts share one component, so "the component defines the signal" is not enough to pick
 * the right unit — only instances whose own symbol/footprint declares one of the signal's refs
 * actually contain it. Among those, prefers an instance where the signal is connected to a net.
 *
 * @param signalId   - The signal id to locate.
 * @param modules    - The candidate design modules (already filtered to the reference designator).
 * @param components - The component map for signal/definition lookup.
 * @param usesPads   - Whether the active drawing renders footprint pads (PCB) rather than symbol pins.
 * @returns The instance that contains one of the signal's pins/pads, or `undefined` if none does.
 */
export function findTargetModuleForSignal(
	signalId: SignalId,
	modules: ZoomSelectionModule[],
	components: ZoomSelectionComponents,
	usesPads: boolean,
): ZoomSelectionModule | undefined {
	const kind = usesPads ? 'footprintPad' : 'symbolPin';
	const signalRefs = (module: ZoomSelectionModule): string[] => {
		const signal = components[module.componentRef]?.signals.find((s) => s.id === signalId);
		if (signal === undefined) {
			return [];
		}
		return usesPads ? signal.padRefs : signal.pinRefs.filter((r): r is string => r !== null);
	};

	const containing = modules.filter((m) => signalRefs(m).some((ref) => moduleContainsPin(m, components, ref, kind)));
	if (containing.length === 0) {
		return undefined;
	}

	const connected = containing.find((m) => signalRefs(m).some((ref) => m.pinNets[ref] !== undefined));
	return connected ?? containing[0];
}

/**
 * Translates a CAD-renderer zoom event into the ordered list of property selections to apply.
 *
 * This is the heart of the "click a module chip → select + zoom" behaviour. The returned commands
 * describe a *partial* selection (a single pin or pad, or the pins/pads of a signal) when the event
 * names a `symbolPin`, `footprintPad`, or `signalId`, and a *full* module selection otherwise. The
 * caller applies the first command non-additively and the rest additively, then zooms to whatever
 * ended up selected.
 *
 * The reference designator may be project-level (`"U1"`, selecting every instance U1A/U1B/…) or
 * design-level (`"U1A"`, selecting only that unit); both are matched against `name` and
 * `fullModuleName`.
 *
 * @param detail     - The zoom event detail (reference designator plus optional pin/pad/signal/net).
 * @param design     - The active design: its `modelType` (pins are rendered on schematics, pads on
 *                     PCBs) and its modules.
 * @param components - The component map for pin/pad/signal resolution.
 * @returns The selections to apply in order; empty when nothing resolvable was specified.
 */
export function resolveZoomSelection(
	detail: Pick<
		CadRendererZoomEventDetail,
		'moduleReferenceDesignator' | 'net' | 'signalId' | 'symbolPin' | 'footprintPad'
	>,
	design: { modelType: string; modules: ZoomSelectionModule[] },
	components: ZoomSelectionComponents,
): ZoomSelectionCommand[] {
	if (detail.moduleReferenceDesignator !== undefined && detail.moduleReferenceDesignator !== '') {
		const matchingModules = design.modules.filter(
			(m) => m.name === detail.moduleReferenceDesignator || m.fullModuleName === detail.moduleReferenceDesignator,
		);

		if (detail.symbolPin !== undefined) {
			const target = findTargetModuleForPin(detail.symbolPin, matchingModules, components, 'symbolPin');
			if (target === undefined) {
				return [];
			}
			return [{ property: DrawingProperties.Pin, value: pinPropertyKey(target.ref, detail.symbolPin) }];
		}

		if (detail.footprintPad !== undefined) {
			const target = findTargetModuleForPin(detail.footprintPad, matchingModules, components, 'footprintPad');
			if (target === undefined) {
				return [];
			}
			return [{ property: DrawingProperties.Pad, value: pinPropertyKey(target.ref, detail.footprintPad) }];
		}

		if (detail.signalId !== undefined) {
			// The active drawing renders either symbol pins (schematic) or footprint pads (PCB);
			// selecting the matching property scopes the selection to what's actually visible.
			const usesPads = design.modelType === 'pcb';
			const kind = usesPads ? 'footprintPad' : 'symbolPin';
			const property = usesPads ? DrawingProperties.Pad : DrawingProperties.Pin;

			// Build one command per (module, ref) pair across all matching instances. A signal can span
			// multiple units (U1A/U1B), so a single target module would drop refs owned by other units.
			const commands: ZoomSelectionCommand[] = [];
			for (const module of matchingModules) {
				const signal = components[module.componentRef]?.signals.find((s) => s.id === detail.signalId);
				if (!signal) {
					continue;
				}
				const allRefs = usesPads ? signal.padRefs : signal.pinRefs.filter((r): r is string => r !== null);
				for (const ref of allRefs) {
					if (moduleContainsPin(module, components, ref, kind)) {
						commands.push({ property, value: pinPropertyKey(module.ref, ref) });
					}
				}
			}
			return commands;
		}

		return matchingModules.map((m) => ({ property: DrawingProperties.ObjectId, value: m.name }));
	}

	if (detail.net !== undefined && detail.net !== '') {
		return [{ property: DrawingProperties.Net, value: detail.net }];
	}

	return [];
}
